/**
 * DraggableMap.js
 * A Google Maps component with a draggable marker and satellite/roadmap toggle.
 * When the user drags the pin, the address field is updated via reverse geocoding.
 *
 * Props:
 *   lat         {number}   Initial latitude (default: 24.7136 = Riyadh)
 *   lng         {number}   Initial longitude (default: 46.6753 = Riyadh)
 *   address     {string}   Current address value (controlled)
 *   onLocationChange({lat, lng, address, city})  Called when pin is dragged
 *   height      {string}   CSS height of the map container (default: "280px")
 */

import { useEffect, useRef, useState } from "react";

const GOOGLE_MAPS_API_KEY = "AIzaSyB1oST8BuCKK4jfZ_Pev5XxBC6_VK1i7Xw";

let mapsScriptLoaded = false;
let mapsScriptCallbacks = [];

function loadGoogleMapsScript(callback) {
  if (window.google && window.google.maps) {
    callback();
    return;
  }
  mapsScriptCallbacks.push(callback);
  if (mapsScriptLoaded) return;
  mapsScriptLoaded = true;

  window.__gmapsReady = () => {
    mapsScriptCallbacks.forEach((cb) => cb());
    mapsScriptCallbacks = [];
  };

  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geocoding&callback=__gmapsReady`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

function reverseGeocode(geocoder, lat, lng) {
  return new Promise((resolve) => {
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== "OK" || !results || results.length === 0) {
        resolve({ address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`, city: "" });
        return;
      }
      const result = results[0];
      const address = result.formatted_address || "";
      // Extract city from address components
      let city = "";
      const cityTypes = ["locality", "administrative_area_level_2", "administrative_area_level_1"];
      for (const type of cityTypes) {
        const comp = result.address_components.find((c) => c.types.includes(type));
        if (comp) { city = comp.long_name; break; }
      }
      resolve({ address, city });
    });
  });
}

const DraggableMap = ({
  lat = 24.7136,
  lng = 46.6753,
  address = "",
  onLocationChange,
  height = "280px",
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [hint, setHint] = useState(false);
  const [mapType, setMapType] = useState("roadmap"); // "roadmap" | "satellite"

  useEffect(() => {
    loadGoogleMapsScript(() => {
      if (!mapRef.current) return;
      const center = { lat, lng };
      const map = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 15,
        mapTypeId: "roadmap",
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER,
        },
      });
      mapInstanceRef.current = map;
      geocoderRef.current = new window.google.maps.Geocoder();

      const marker = new window.google.maps.Marker({
        position: center,
        map,
        draggable: true,
        animation: window.google.maps.Animation.DROP,
        title: "Drag to set exact location",
        icon: {
          url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
          scaledSize: new window.google.maps.Size(40, 40),
        },
      });
      markerRef.current = marker;
      setLoading(false);

      marker.addListener("dragstart", () => setHint(false));
      marker.addListener("dragend", async () => {
        const pos = marker.getPosition();
        const newLat = pos.lat();
        const newLng = pos.lng();
        const { address: newAddress, city } = await reverseGeocode(geocoderRef.current, newLat, newLng);
        if (onLocationChange) {
          onLocationChange({ lat: newLat, lng: newLng, address: newAddress, city });
        }
      });

      // Show hint after 1.5s
      setTimeout(() => setHint(true), 1500);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update marker position when lat/lng props change (e.g. from address search)
  useEffect(() => {
    if (markerRef.current && mapInstanceRef.current) {
      const pos = { lat, lng };
      markerRef.current.setPosition(pos);
      mapInstanceRef.current.panTo(pos);
    }
  }, [lat, lng]);

  // Toggle map type
  const toggleMapType = () => {
    const next = mapType === "roadmap" ? "satellite" : "roadmap";
    setMapType(next);
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setMapTypeId(next);
    }
  };

  return (
    <div style={{ position: "relative", borderRadius: "12px", overflow: "hidden", border: "1.5px solid #e2e0d8", height }}>
      {loading && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
          background: "#f0efe8", zIndex: 10, fontSize: 13, color: "#9a9a8e",
        }}>
          <svg style={{ width: 20, height: 20, marginRight: 8, animation: "spin 1s linear infinite" }} viewBox="0 0 24 24" fill="none" stroke="#c9a84c" strokeWidth="2">
            <circle cx="12" cy="12" r="10" strokeDasharray="40" strokeDashoffset="10" />
          </svg>
          Loading map…
        </div>
      )}
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />

      {/* Satellite / Map toggle button */}
      {!loading && (
        <button
          type="button"
          onClick={toggleMapType}
          title={mapType === "roadmap" ? "Switch to Satellite view" : "Switch to Map view"}
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.3px",
            background: mapType === "satellite" ? "#0e3725" : "#fff",
            color: mapType === "satellite" ? "#f8b602" : "#0e3725",
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
            transition: "background 0.2s, color 0.2s",
          }}
        >
          {mapType === "roadmap" ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              Satellite
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
              Map
            </>
          )}
        </button>
      )}

      {hint && !loading && (
        <div style={{
          position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)",
          background: "rgba(14,55,37,0.85)", color: "#fff", fontSize: 11, fontWeight: 600,
          padding: "5px 12px", borderRadius: 20, pointerEvents: "none", whiteSpace: "nowrap",
          backdropFilter: "blur(4px)",
        }}>
          📍 Drag the pin to set the precise location
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default DraggableMap;
